import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const PythonBooleans = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Logic & Flow</div>
                <h1>Boolean Logic & Truthiness</h1>
                <p className="hero-lead">
                    Master the digital logic gates that control your code. From the "Lazy Guard" behavior of short-circuits
                    to the powerful "Truthiness" system that allows any Python object to make a decision.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    At the heart of every decision your computer makesfrom verifying your password to guiding a rocketlies
                    a simple question: <strong>True or False?</strong>
                </p>
                <p>
                    In Python, this logic system is more flexible than in strict languages like C++ or Java. Python doesn't just
                    deal with strict booleans; it evaluates the "Truthiness" of every object. This allows you to write
                    incredibly expressive code like <code>if user_name:</code> instead of checking string length.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Logic Gates:</strong> Mastering <code>and</code>, <code>or</code>, and <code>not</code>.</li>
                        <li><strong>The Lazy Guard:</strong> How Short-Circuit evaluation saves CPU cycles (and crashes).</li>
                        <li><strong>Truthiness:</strong> Why <code>""</code> is False but <code>"0"</code> is True.</li>
                        <li><strong>Bitwise vs Logical:</strong> The critical difference between <code>and</code> and <code>&</code>.</li>
                        <li><strong>De Morgan's Laws:</strong> How to simplify complex negative logic (<code>not A or not B</code>).</li>
                        <li><strong>Internals:</strong> Why <code>True</code> is a singleton in memory.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Logic Gates: The Club Bouncer Analogy</h2>
                <p>
                    Think of Boolean operators as rules given to a Bouncer at a night club.
                </p>

                <div className="analogy-box">
                    <h3> Analogy: The Club Bouncer</h3>
                    <div className="analogy-grid">
                        <div className="analogy-item">
                            <div className="analogy-icon"></div>
                            <strong>AND (Strict)</strong>
                            <p>
                                "You need an ID <strong>AND</strong> a Ticket."
                                <br />
                                If you miss <em>either</em>, you don't get in. Both must be True.
                            </p>
                        </div>
                        <div className="analogy-item">
                            <div className="analogy-icon"></div>
                            <strong>OR (Flexible)</strong>
                            <p>
                                "You need a VIP Pass <strong>OR</strong> $20 cover charge."
                                <br />
                                If you have <em>either</em> (or both), you get in. Only fails if you have neither.
                            </p>
                        </div>
                        <div className="analogy-item">
                            <div className="analogy-icon"></div>
                            <strong>NOT (The Denier)</strong>
                            <p>
                                "If you are <strong>NOT</strong> on the banned list."
                                <br />
                                It simply flips the result. True becomes False, False becomes True.
                            </p>
                        </div>
                    </div>
                </div>

                <CodeBlock language="python" code={`has_id = True
has_ticket = False
is_vip = True
has_cash = False

# AND: Strict
print(has_id and has_ticket) # False (Missing ticket)

# OR: Flexible
print(is_vip or has_cash)    # True (VIP is enough)

# NOT: Inversion
is_banned = False
print(not is_banned)         # True (Access functionality)`} />
            </div>

            <div className="content-section">
                <h2>Short-Circuit Evaluation: The "Lazy Guard"</h2>
                <p>
                    Python is efficient (or lazy). It stops thinking the moment it knows the answer. This behavior,
                    called <strong>Short-Circuit Evaluation</strong>, is crucial for safety.
                </p>

                <h3>1. The `and` Short-Circuit</h3>
                <p>
                    If the first condition is <code>False</code>, Python <strong>stops</strong> checking. Why check the ID if the user already admitted they have no ticket?
                </p>

                <h3>2. The `or` Short-Circuit</h3>
                <p>
                    If the first condition is <code>True</code>, Python <strong>stops</strong> checking. Why check for cash if the user already showed a VIP pass?
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Safety through Laziness</span>
                    </div>
                    <CodeBlock language="python" code={`# Dangerous Code without Short-Circuiting
denominator = 0

# Python sees "denominator != 0" is False.
# It STOPS. It never calculates "10 / denominator".
# If it didn't stop, the program would crash with ZeroDivisionError!
if denominator != 0 and (10 / denominator) &gt; 1:
    print("Safe calculation")
else:
    print("Skipped to avoid crash")

# Practical Use: Default Values
user_name = ""
# If user_name is empty (False), it proceeds to use "Anonymous"
display_name = user_name or "Anonymous"
print(display_name)  # "Anonymous"`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Truthiness: The Universal Boolean</h2>
                <p>
                    In Python, you don't need to check <code>if len(my_list) {'>'} 0</code>.
                    Every object has a "Truth" value.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Evaluates to False (Falsy)</th>
                                <th>Evaluates to True (Truthy)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Numbers</strong></td>
                                <td><code>0</code>, <code>0.0</code></td>
                                <td>Any non-zero number (<code>1</code>, <code>-5</code>, <code>3.14</code>)</td>
                            </tr>
                            <tr>
                                <td><strong>Strings</strong></td>
                                <td><code>""</code> (Empty)</td>
                                <td>Any character (<code>"a"</code>, <code>" "</code>, <code>"0"</code>)</td>
                            </tr>
                            <tr>
                                <td><strong>Containers</strong></td>
                                <td><code>[]</code>, <code>{'{'}{'}'}</code>, <code>()</code>, <code>set()</code></td>
                                <td>Any non-empty container</td>
                            </tr>
                            <tr>
                                <td><strong>The Void</strong></td>
                                <td><code>None</code></td>
                                <td>-</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Interview Logic: AND vs & (Ampersand)</h2>
                <p>
                    This is a favorite interview question. What is the difference between <code>and</code> and <code>&</code>?
                </p>
                <ul>
                    <li><code>and</code> is a <strong>Logical Operator</strong>. It checks truthiness and short-circuits.</li>
                    <li><code>&</code> is a <strong>Bitwise Operator</strong>. It compares bits (arithmetic) and NEVER short-circuits.</li>
                </ul>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Performance Trap</span>
                    </div>
                    <CodeBlock language="python" code={`# 1. Logical 'and'
# Checks if 5 is truthy -&gt; Yes. Returns 4.
print(5 and 4)  # 4

# 2. Bitwise '&'
# 5 is 101 binary
# 4 is 100 binary
# 101 & 100 = 100 (which is 4)
print(5 & 4)    # 4 (Coincidence!)

#  THE DANGER ZONE
def slow_check():
    print("Checking...")
    return False

# with 'and', slow_check() is SKIPPED because first part is False
if False and slow_check():
    pass 

# with '&', slow_check() executes! No short-circuiting!
if False & slow_check():
    pass 
# Output: "Checking..."`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Deep Dive: The Singleton Truth</h2>
                <p>
                    <code>True</code> and <code>False</code> are not just values; they are <strong>Singletons</strong>.
                    This means there is only one <code>True</code> object in the entire memory of your Python program.
                </p>
                <CodeBlock language="python" code={`x = True
y = 10 &gt; 5  # Evaluates to True

# They are the exact same object in memory
print(id(x) == id(y))  # True

# They are actually keywords in Python 3 cannot be reassigned
# True = 5  # SyntaxError: cannot assign to True`} />
            </div>

            <div className="content-section">
                <h2>Advanced Logic: De Morgan's Laws</h2>
                <p>
                    When logic gets complex, negative conditions become hard to read.
                    Augustus De Morgan gave us two rules to simplify them.
                </p>
                <ul>
                    <li><code>not (A or B)</code> is the same as <code>(not A) and (not B)</code></li>
                    <li><code>not (A and B)</code> is the same as <code>(not A) or (not B)</code></li>
                </ul>

                <div className="code-example">
                    <div className="code-header">
                        <span>Refactoring Negative Logic</span>
                    </div>
                    <CodeBlock language="python" code={`raining = True
snowing = False

#  Hard to read
if not (raining or snowing):
    print("Go outside")

# Equivalent and clearer (De Morgan's Transformation)
if (not raining) and (not snowing):
    print("Go outside")`} />
                </div>
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices & Takeaways</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use Truthiness: <code>if my_list:</code> instead of <code>len(my_list) {'>'} 0</code>.</li>
                            <li>Use Short-Circuit for safety: <code>if user and user.is_active:</code>.</li>
                            <li>Use <code>any()</code> and <code>all()</code> for list checks.</li>
                            <li>Simplify <code>not (A or B)</code> using De Morgan's laws.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't compare checks to True: <code>if allowed == True:</code> (Redundant).</li>
                            <li>Don't use <code>&</code> (Bitwise) when you mean <code>and</code> (Logical).</li>
                            <li>Don't nest ternary operators (Unreadable). Keep it simple.</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonBooleans;



